import React, { FC } from 'react';
import {
  ClaimMilestoneError,
  RejectMilestoneError,
  ReclaimMilestoneError,
  ApproveMilestoneError,
  UnclaimMilestoneError,
  UnApproveMilestoneError,
  CommodityWrapper,
  CancelFitupMilestoneError,
} from '@bechtel/types';
import { Box, Text, Spinner } from '@bechtel/gds';
import { GET_WORKPACKAGE_DETAIL_BY_IDS, GET_COMMODITIES_BY_COMMODITY_IDS } from '@bechtel/client-gql';
import { useWorkPackage } from '~/detail/context';
import { useCommodity } from '~/detail/commodities/context';
import { Modal, ModalHeader, ModalBody, ModalFooter, useModalState, Button, ButtonAppearance } from '@bechtel/gds';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
interface MilestoneErrorProps {
  milestoneError:
    | ClaimMilestoneError
    | RejectMilestoneError
    | ReclaimMilestoneError
    | ApproveMilestoneError
    | UnclaimMilestoneError
    | UnApproveMilestoneError
    | CancelFitupMilestoneError
    | ApolloError;
}

//TODO: More specific errors needs to be added for different mutation errors.
const getErrorMessage = (error: MilestoneErrorProps['milestoneError']) => {
  if (error instanceof ApolloError)
    return (
      <Trans>
        Network error has occured.Please press the &ldquo;Retry&rdquo; button to fetch the latest data and try again.
      </Trans>
    );
  switch (error) {
    case ClaimMilestoneError.MILESTONE_IS_ALREADY_CLAIMED:
    case ReclaimMilestoneError.MILESTONE_IS_CLAIMED:
    case CancelFitupMilestoneError.MILESTONE_IS_CLAIMED:
      return (
        <Trans>
          This milestone has already been claimed by you or another user. Please press the &ldquo;Update&rdquo; button
          to fetch the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.MILESTONE_IS_APPROVED:
    case ReclaimMilestoneError.MILESTONE_IS_APPROVED:
    case ApproveMilestoneError.MILESTONE_IS_APPROVED:
    case RejectMilestoneError.MILESTONE_IS_APPROVED:
    case UnclaimMilestoneError.MILESTONE_IS_APPROVED:
    case CancelFitupMilestoneError.MILESTONE_IS_APPROVED:
      return (
        <Trans>
          This milestone has already been approved. Please press the &ldquo;Update&rdquo; button to fetch the latest
          data and try again.
        </Trans>
      );
    case ClaimMilestoneError.MILESTONE_IS_REJECTED:
    case RejectMilestoneError.MILESTONE_IS_REJECTED:
    case UnclaimMilestoneError.MILESTONE_IS_REJECTED:
    case UnApproveMilestoneError.MILESTONE_IS_REJECTED:
      return (
        <Trans>
          This milestone has already been rejected. Please press the &ldquo;Update&rdquo; button to fetch the latest
          data and try again.
        </Trans>
      );
    case RejectMilestoneError.MILESTONE_NOT_CLAIMED:
    case ApproveMilestoneError.MILESTONE_NOT_CLAIMED:
    case UnclaimMilestoneError.MILESTONE_NOT_CLAIMED:
      return (
        <Trans>
          This milestone cannot be uclaimed/approved/rejected as it is not claimed. Please press the
          &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.MILESTONE_IS_BLOCKED:
    case RejectMilestoneError.MILESTONE_IS_BLOCKED:
    case ReclaimMilestoneError.MILESTONE_IS_BLOCKED:
    case ApproveMilestoneError.MILESTONE_IS_BLOCKED:
    case UnclaimMilestoneError.MILESTONE_IS_BLOCKED:
    case UnApproveMilestoneError.MILESTONE_IS_BLOCKED:
    case CancelFitupMilestoneError.MILESTONE_IS_BLOCKED:
      return (
        <Trans>
          This milestone is blocked. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try
          again.
        </Trans>
      );
    case UnApproveMilestoneError.MILESTONE_IS_NOT_APPROVED:
      return (
        <Trans>
          This milestone cannot be unapproved as it is not approved. Please press the &ldquo;Update&rdquo; button to
          fetch the latest data and try again.
        </Trans>
      );
    case ReclaimMilestoneError.NOT_FOUND:
    case RejectMilestoneError.NOT_FOUND:
    case ApproveMilestoneError.NOT_FOUND:
    case UnclaimMilestoneError.NOT_FOUND:
    case UnApproveMilestoneError.NOT_FOUND:
    case ClaimMilestoneError.NOT_FOUND:
    case CancelFitupMilestoneError.NOT_FOUND:
      return (
        <Trans>
          This milestone is not found. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try
          again.
        </Trans>
      );
    case ApproveMilestoneError.EIS_ERROR:
    case UnApproveMilestoneError.EIS_ERROR:
    case ClaimMilestoneError.EIS_ERROR:
      return (
        <Trans>
          An error has occured updating the destination systems. Please press the &ldquo;Update&rdquo; button to fetch
          the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.PREVIOUS_MILESTONE_NOT_CLAIMED_YET:
      return (
        <Trans>
          This milestone cannot be claimed since the previous milestone has not been claimed yet. Please press the
          &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.ASSOCIATED_WELDS_NOT_APPROVED:
      return (
        <Trans>
          The associated weld has not been approved yet. Please press the &ldquo;Update&rdquo; button to fetch the
          latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.MISSING_REQUIRED_PERMISSION:
    case RejectMilestoneError.MISSING_REQUIRED_PERMISSION:
    case ReclaimMilestoneError.MISSING_REQUIRED_PERMISSION:
    case ApproveMilestoneError.MISSING_REQUIRED_PERMISSION:
    case UnclaimMilestoneError.MISSING_REQUIRED_PERMISSION:
    case UnApproveMilestoneError.MISSING_REQUIRED_PERMISSION:
    case CancelFitupMilestoneError.MISSING_REQUIRED_PERMISSION:
      return (
        <Trans>
          You are missing the required permission to perform this action.Please press the &ldquo;Update&rdquo; button to
          fetch the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.SERVICE_PROXY_ERROR:
    case RejectMilestoneError.SERVICE_PROXY_ERROR:
    case ReclaimMilestoneError.SERVICE_PROXY_ERROR:
    case ApproveMilestoneError.SERVICE_PROXY_ERROR:
    case UnclaimMilestoneError.SERVICE_PROXY_ERROR:
    case UnApproveMilestoneError.SERVICE_PROXY_ERROR:
      return (
        <Trans>
          Service Proxy Error.Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case ClaimMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
    case RejectMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
    case ReclaimMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
    case ApproveMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
    case UnclaimMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
    case UnApproveMilestoneError.MILESTONE_CONFIG_NOT_FOUND:
      return (
        <Trans>
          Milestone config not found.Please press the &ldquo;Update&rdquo; button to fetch the latest data and try
          again.
        </Trans>
      );
    case ReclaimMilestoneError.MILESTONE_NOT_REJECTED:
      return (
        <Trans>
          Milestone has not been rejected yet.Please press the &ldquo;Update&rdquo; button to fetch the latest data and
          try again.
        </Trans>
      );
    case UnclaimMilestoneError.MILESTONE_CLAIMED_BY_SOME_OTHER_USER:
      return (
        <Trans>
          Milestone has already been claimed by some other user.Please press the &ldquo;Update&rdquo; button to fetch
          the latest data and try again.
        </Trans>
      );
    case UnApproveMilestoneError.MILESTONE_APPROVED_BY_SOME_OTHER_USER:
      return (
        <Trans>
          Milestone has already been approved by some other user.Please press the &ldquo;Update&rdquo; button to fetch
          the latest data and try again.
        </Trans>
      );
    case CancelFitupMilestoneError.CANCEL_FITUP_REQUEST_ERRORED:
      return <Trans>Some error has occured. Please try again after sometime.</Trans>;
    case CancelFitupMilestoneError.FITUP_IS_CANCELED:
      return <Trans>Fitup request is already cancelled.</Trans>;
    case CancelFitupMilestoneError.INVALID_MILESTONE_ACTION:
      return <Trans>Action on invalid milestone</Trans>;
    default:
      return (
        <Trans>
          An error has occured. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
  }
};

export const MilestoneError: FC<MilestoneErrorProps> = ({ milestoneError }) => {
  const { id } = useWorkPackage();
  const { id: commodityId } = useCommodity();
  const { visible, close } = useModalState(true);
  const [loadWorkPackagebyId, { loading }] = useLazyQuery(GET_WORKPACKAGE_DETAIL_BY_IDS, {
    variables: {
      ids: [id],
    },
    fetchPolicy: 'network-only',
  });

  const [getCommoditiesByIds] = useLazyQuery<{ commodities: CommodityWrapper[] }, { ids: string[] }>(
    GET_COMMODITIES_BY_COMMODITY_IDS,
    {
      variables: {
        ids: [commodityId],
      },
      fetchPolicy: 'network-only',
    }
  );

  return (
    <Modal visible={visible}>
      <ModalHeader title={t`Milestone Error`}></ModalHeader>
      <ModalBody>
        <Box px={{ sm: '500' }} py={{ sm: '700' }}>
          <Text size="600">{getErrorMessage(milestoneError)}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button appearance={ButtonAppearance.SECONDARY} onClick={close}>
              <Trans> Close</Trans>
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={async () => {
                await loadWorkPackagebyId();
                await getCommoditiesByIds();
                await close();
              }}
              disabled={loading}
            >
              {loading && <Spinner></Spinner>}
              {!loading && milestoneError instanceof ApolloError ? t`Retry` : t`Update`}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
